<template>
  <div
    v-if="loadingGmf"
    class="flex-1 f-center anim-fade-in_slow"
  >
    <LoaderWLogo />
  </div>
  <div
    v-else
    id="practice-trainer"
    class="practice-trainer-layout"
    data-tst="practice-trainer-module"
  >
    <TrainerConfigureGame
      v-if="handsConfigure"
      class="pratralay_left"
      @start="startSession"
    />

    <CollapsePanel
      v-if="!handsConfigure"
      v-bind="panelsProps.panel_left"
      ref="panel_left"
      class="pratralay_left"
      :persistKey="'panel-trainer-left'"
      :style="`--transition-duration: ${multipleTables ? 0 : 0.22}s`"
      tstPrefix="trainer_configure_game"
    >
      <template #content="{ open }">
        <TrainerControls
          :namespace="'tra/alpha'"
          :activeNamespace="namespace"
          :queryStandoff="queryStandoff"
          :hasPresetName="!multipleTables"
          :hasKeydown="!panelReplay"
          :closed="!open"
          :canClose="open"
          class="z-index_4"
          :verticalMinified="!open"
          :preferencesRouterParams="{
            push: false,
            replace: true,
            saveLocally: true,
          }"
        />
        <TrainerStatsExpand
          v-if="!mobile"
          :closed="!open"
          :stats="stats"
          :canClose="open"
        />
      </template>
    </CollapsePanel>

    <TrainersTemplate
      v-if="!handsConfigure"
      :instances="instances"
      :activeInstance="activeInstance"
      :hasCenterCross="multipleTables"
      class="pratralay_center"
      @mouseenter="ins => setActiveNamespace(`tra/${ins}`)"
      @click="handleTrainersTemplateClick"
    >
      <template
        v-for="(instance, idx) in instances"
        #[getSlotName(instance)]
        :key="instance"
      >
        <Trainer
          :key="instance"
          :namespace="`tra/${instance}`"
          :active="instance === activeInstance"
          :grouping="grouping"
          :handsConfigure="handsConfigure"
          :hasSounds="hasSounds"
          :tstPrefix="`trainer_table_${idx}`"
        />
      </template>
      <template #center>
        <div
          v-if="multipleTables"
          class="centered-stats"
        >
          <div
            class="centered-stats_content"
            :class="[
              { 'centered-stats_content_no-dot': !activeInstance },
              `centered-stats_content_tables_${numOfTables} pratralay_active-table_${activeTableIdx}`,
            ]"
          >
            <div class="pratralay_active-table_dot csst f-center">
              <div
                class="csst_content"
                style="bottom: -2.5rem"
              >
                <span class="text-nowrap pr-2">
                  {{ $t('tooltips.tables_indicator') }}
                </span>
                <span class="keyboardkey_key clr-white">↑</span>
                <span class="keyboardkey_key clr-white">↓</span>
              </div>
            </div>
            <TrainerStreak
              v-if="hasStreak"
              :streak="streak"
              class="pratralay_streak"
            />
            <TrainerSessionCounter
              v-if="hasSessionCounter"
              class="pratralay_session-counter"
              :sessionHand="sessionCount.current_hands"
              :sessionMaxHands="sessionCount.max_hands"
            />
            <div
              v-if="timebank"
              v-ripple
              data-tst="trainer_central_timebank"
              class="text-nowrap gw_btn gw_btn_texts mdi mdi-stop-circle-outline btn-stop-timebank text-small transition_3 csst"
              :class="{ ' btn-stop-timebank_disabled': !timebankRunning }"
              @click="stopTimebank"
            >
              <i class="mr-1 mdi mdi-stop-circle-outline text-normal mr-1"></i>
              {{ $t('btn.stop') }}
              <div
                class="csst_content text-nowrap"
                style="top: -1.5rem; line-height: 1"
                v-html="$t('common.stop_timebank')"
              ></div>
            </div>
          </div>
        </div>
        <div
          v-if="sessionFinished"
          class="pratralay_session-finished-btns text-normal"
        >
          <div class="weight_600 text-large mb-6">
            {{ $t('messages.trainer_session_finished') }}
          </div>
          <TrainerConfirmBtns
            v-bind="{
              hasCloseSession: true,
              hasRestartHands: true,
            }"
            @closeSession="endSession"
            @restartHands="startSession"
          />
        </div>
        <TrainerStreakPrompt
          v-if="streakVisible"
          :streak="streak"
        />
      </template>
    </TrainersTemplate>

    <CollapsePanel
      v-show="!handsConfigure"
      v-bind="panelsProps.panel_right"
      ref="panel_right"
      class="tralay_right"
      persistKey="panel-trainer-right"
      style=""
      :style="`
      --p-replay-left: var(--width-visible);
      --transition-duration: ${multipleTables ? 0 : 0.22}s;
      --max-height-info: var(--vh-10);
      --max-height-history: var(--vh-10);
      `"
    >
      <template #before>
        <TrainerReplay
          v-if="!mobile"
          :namespace="'tra/replay'"
          style="z-index: 1"
          :open="panelReplay"
          :sessionInProgress="!handsConfigure"
        />
      </template>
      <template #content="v">
        <TrainerHintsExpand
          ref="hints_expand"
          :hintsProps="hintsPropsNamespace"
          :hasContent="!multipleTables"
          :closed="!v.open"
        >
          <div
            v-if="multipleTables"
            class="f-center f-column pa-4 clr-grey"
          >
            <i class="mdi text-semi-large mdi-information-outline trapresets_info_icon" />
            <div
              class="mx-auto text-medium text-center"
              v-html="$t('messages.info_disabled_multitable')"
            ></div>
          </div>
        </TrainerHintsExpand>

        <TrainerStatsExpand
          v-if="mobile && !multipleTables"
          ref="stats_expand"
          style="--pos-sticky-top: 2rem"
          :closed="!v.open"
          :stats="stats"
        />
        <TrainerHistory
          ref="history_expand"
          :style="mobile ? '--pos-sticky-top: 4rem' : '--pos-sticky-top: 2rem'"
          :closed="!v.open"
          :panelReplayOpen="panelReplay"
        />
      </template>
    </CollapsePanel>

    <TrainerHintsFloat
      v-if="multipleTables"
      :hintsProps="hintsPropsFloat"
      :activeNamespace="namespace"
    />

    <transition name="sessionfinish">
      <TrainerSessionOverview
        v-if="statsSessionResultVisible"
        :item="statsSessionOverviewItem"
        :isCurrent="statsSessionOverviewItem.uid === sessionId"
        class="pratralay_session-message"
        @end:session="endSession"
        @repeat:session="repeatSession"
        @close="closeSessionOverview"
        @show:history="showHistory"
        @show:hand="showHand"
      >
      </TrainerSessionOverview>
    </transition>
    <transition name="sessionfinish">
      <MessageOverlaySkeleton
        v-if="sessionError"
        class="tralay_message-overlay_back"
        :isBlurred="true"
      >
        <MessagesTrainerError
          :namespace="namespace"
          :item="sessionError"
        />
        <TrainerConfirmBtns
          class="mt-4 f-wrap"
          v-bind="{
            hasStopHands: true,
            hasRestartHands: true,
          }"
          @stopHands="endSession"
          @restartHands="startSession"
        />
      </MessageOverlaySkeleton>
    </transition>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import TrainerConfigureGame from '@/components/Trainer/TrainerConfigureGame'
import Trainer from '@/components/Trainer/Trainer'
import TrainersTemplate from '@/components/Trainer/TrainersTemplate'
import CollapsePanel from '@/components/CollapsePanel/CollapsePanel'
import TrainerControls from '@/components/Trainer/TrainerControls'
import TrainerHistory from '@/components/Trainer/TrainerHistory'
import TrainerStatsExpand from '@/components/Trainer/TrainerStatsExpand'
import TrainerSessionOverview from '@/components/Trainer/TrainerSessionOverview'
import TrainerStreak from '@/components/Trainer/TrainerStreak'
import TrainerSessionCounter from '@/components/Trainer/TrainerSessionCounter'
import TrainerHintsExpand from '@/components/Trainer/TrainerHintsExpand'
import TrainerReplay from '@/components/Trainer/TrainerReplay'
import TrainerHintsFloat from '@/components/Trainer/TrainerHintsFloat'
import TrainerStreakPrompt from '@/components/Trainer/TrainerStreakPrompt'
// import TrainerBackPattern from '@/components/Trainer/TrainerBackPattern'
import MessageOverlaySkeleton from '@/components/Poker/Overlays/MessageOverlaySkeleton'
import TrainerConfirmBtns from '@/components/Trainer/TrainerConfirmBtns'
import MessagesTrainerError from '@/components/Poker/Overlays/Messages/MessagesTrainerError'
import LoaderWLogo from '@/components/Loaders/LoaderWLogo.vue'

export default {
  name: 'PracticeTrainer',
  components: {
    TrainerConfigureGame,
    Trainer,
    TrainersTemplate,
    CollapsePanel,
    TrainerControls,
    TrainerStatsExpand,
    TrainerSessionOverview,
    TrainerStreak,
    TrainerSessionCounter,
    TrainerHistory,
    TrainerHintsExpand,
    TrainerReplay,
    TrainerHintsFloat,
    TrainerStreakPrompt,
    MessageOverlaySkeleton,
    TrainerConfirmBtns,
    MessagesTrainerError,
    LoaderWLogo,
  },
  data() {
    this.hasSounds = true
    this.allNamespaces = ['tra/alpha', 'tra/beta', 'tra/gamma', 'tra/delta']
    return {
      loadingGmf: true,
    }
  },
  async created() {
    if (this.handsConfigure) {
      await this.$store.dispatch('gameformats_init_pageViewStores')
    }
    this.loadingGmf = false
  },
  methods: {
    getSlotName(instance) {
      return `slot_${instance}`
    },
    startSession() {
      this.$store.dispatch(`hands_start_session`)
    },
    endSession() {
      this.$store.dispatch(`hands_stop`)
    },
    repeatSession(item) {
      const sessionSettings = item.session_state.session_settings
      this.$store.dispatch(`hands_start_session`, { sessionSettings })
    },
    closeSessionOverview() {
      this.$store.commit(`stats_set`, { key: 'stats_session_result_visible', value: false })
    },
    showHand(hand) {
      this.showHistory()
      nextTick(() => {
        // debugger
        this.$refs.history_expand.openInReplayer(hand, 'keepReplayOpen')
      })
    },
    stopTimebank() {
      this.$store.dispatch('hands_clear_timeouts')
    },
    showHistory() {
      this.closeSessionOverview()
      this.$refs.panel_right.toggle(true)
      this.$refs.history_expand.$refs.history_expand_card.open()
      if (this.$refs.hints_expand) {
        this.$refs.hints_expand.$refs.hints_expand_card.close()
      }
      if (this.mobile) {
        this.$refs.stats_expand.$refs.stats_expand_card.close()
        this.$store.commit(`panels_set`, { key: 'panel_left', value: false })
        this.$store.commit(`panels_set`, { key: 'panel_right', value: true })
        this.$store.commit(`tra/alpha/trainer_set`, { key: 'trainer_panel_left', value: false })
        this.$store.commit(`tra/alpha/trainer_set`, { key: 'trainer_panel_right', value: true })
      }
    },
    setActiveNamespace(nn) {
      this.$store.dispatch(`hands_set_active_namespace`, { namespace: nn })
    },
    moveTableFocus(num) {
      const opts = this.$store.getters.active_namespaces
      const idx = opts.findIndex(nn => nn === this.namespace)
      const nn = opts[idx + num] || (num === 1 ? opts[0] : opts[opts.length - 1])
      this.setActiveNamespace(nn)
    },
    handleTrainersTemplateClick() {
      if (this.$store.getters.panel_conditions === 'allHalfOpen') {
        this.$refs.panel_left.setOpen(false)
        this.$refs.panel_right.setOpen(false)
      }
    },
  },
  computed: {
    // TODO fix this fallback - clear log in first play is bugged wihout this
    grouping() {
      return this.$store.getters[`tra/alpha/fh_trainer_grouping`] || 'swv_grouping_none'
    },
    streak() {
      return this.$store.getters.streak
    },
    sessionCount() {
      return this.$store.getters.session_count
    },
    timebank() {
      return this.$store.getters[`tra/alpha/fh_trainer_timebank`]
    },
    timebankRunning() {
      return this.$store.getters.trainer_timebank_running
    },
    numOfTables() {
      return this.$store.getters[`${this.namespace}/fh_trainer_tables`]
    },
    multipleTables() {
      return this.numOfTables > 1
    },
    streakVisible() {
      return this.$store.getters[`tra/alpha/fh_trainer_streak_visible`]
    },
    hasStreak() {
      return this.streakVisible && this.multipleTables
    },
    hasSessionCounter() {
      return this.$store.getters.session_uid && this.multipleTables
    },
    sessionError() {
      return this.$store.getters.session_error
    },
    sessionFinished() {
      return this.$store.getters.session_finished
    },
    panelReplay() {
      return this.$store.getters.panel_replay
    },
    panelsProps() {
      return this.$store.getters.trainer_panels
    },
    hintsPropsFloat() {
      return this.$store.getters.hints_props_float
    },
    hintsPropsNamespace() {
      return this.$store.getters.hints_props_namespace
    },
    stats() {
      return this.$store.getters.stats_session
    },
    statsSessionOverviewItem() {
      return this.$store.getters.stats_session_overview
    },
    statsSessionResultVisible() {
      return this.$store.getters.stats_session_result_visible
    },
    sessionMaxHands() {
      return this.$store.getters.session_max_hands
    },
    sessionId() {
      return this.$store.getters.session_uid
    },
    namespace() {
      return this.$store.getters.active_namespace
    },
    instances() {
      return this.$store.getters.hands_instances
    },
    activeInstance() {
      let res = this.$store.getters.active_instance
      res = this.$store.getters.locked_instances.includes(res) ? '' : res
      return res
    },
    lockedInstances() {
      return this.$store.getters.locked_instances
    },
    activeTableIdx() {
      return this.$store.getters.active_namespace_idx + 1
    },
    queryStandoff() {
      return this.$store.getters[`${this.namespace}/query_trainer_standoff`]
    },
    focusMode() {
      return this.$store.getters.focus_mode
    },
    handsConfigure() {
      return this.$store.getters.hands_configure
    },
    mobile() {
      return this.$store.getters.globals_mobile
    },
    selectedPattern() {
      return this.$store.getters.mergedUserSettings.traineBackPattern
    },
    hotkeysPressedBottom() {
      return this.$store.getters.hotkeys_pressed_bottom
    },
  },
  watch: {
    hotkeysPressedBottom(keys) {
      if (!keys) return
      if (this.handsConfigure) return
      const hotkeyActions = {
        tableFocusNext: { keys: ['arrowright', 'control'], fn: () => this.moveTableFocus(1) },
        tableFocusNext2: { keys: ['arrowdown'], fn: () => this.moveTableFocus(1) },
        tableFocusPrev: { keys: ['arrowleft', 'control'], fn: () => this.moveTableFocus(-1) },
        tableFocusPrev2: { keys: ['arrowup'], fn: () => this.moveTableFocus(-1) },
      }
      this.$store.dispatch('hotkeys_perform_action', { keys, hotkeyActions })
    },
  },
}
</script>
<style lang="scss">
.practice-trainer-layout {
  display: flex;
  flex: 1;
  max-height: var(--vh-3);
  height: var(--vh-3);
  min-height: 0;
  overflow: hidden;
  position: relative;
}
</style>
<style lang="scss" scoped>
.btn-stop-timebank {
  position: absolute;
  top: -2rem;
  --clr-back: var(--clr-black-1);
  --clr: var(--clr-grey-2);
  --clr: var(--clr-white-0);
  transition: 0.22s;
  &_disabled {
    opacity: 0;
    pointer-events: none;
  }
}
.pratralay {
  &_left {
    z-index: 4;
  }
  &_center {
    z-index: 1;
  }
  &_right {
    z-index: 4;
  }
}
.centered-stats {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  &_content {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    grid-gap: 0.5rem;
    padding: 0.75rem;
    font-size: 0.875rem;
    z-index: 1;
    position: relative;
    border-radius: 8px;
    background: var(--clr-black-1);
    transform: translate(0, 0);
    transition: 0.22s;
    &:before {
      // so that when hovering the dot and hitting up/down mouseenter focus doesn't trigger
      content: '';
      position: absolute;
      background: var(--clr-black-1);
      height: calc(100% + 0.75rem);
      width: calc(100% + 0.75rem);
      z-index: 0;
      background: transparent;
    }
    --active-table-dot-inset: auto auto auto auto;
    --mm: -0.5rem;
    --mx: -0.375rem;
    --m: -0.25rem;
    --mh: -0.125rem;

    &_no-dot {
      .pratralay_active-table_dot {
        opacity: 0;
      }
    }
    .pratralay_active-table {
      &_dot {
        position: absolute;
        border-radius: 50%;
        background: var(--clr-primary);
        inset: var(--active-table-dot-inset);
        transition: 0.15s ease-out;
        --size: 0.75rem;
        height: var(--size);
        width: var(--size);
        z-index: 4;
        box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.25);
      }
    }
    &_tables {
      &_2 {
        &.pratralay_active-table {
          &_1 {
            --active-table-dot-inset: calc(50% + var(--m)) 50% 50% var(--mx);
          }
          &_2 {
            --active-table-dot-inset: calc(50% + var(--m)) 50% 50% calc(100% + var(--mx));
          }
        }
      }
      &_3 {
        transform: translate(0, calc(-50% - 0.5rem));
        .centered-stats_content {
          flex-direction: row;
        }
        &.pratralay_active-table {
          &_1 {
            --active-table-dot-inset: var(--m) 50% 50% var(--m);
          }
          &_2 {
            --active-table-dot-inset: var(--m) 50% 50% calc(100% + var(--mx));
          }
          &_3 {
            --active-table-dot-inset: calc(100% + var(--mx)) 50% 50% calc(50% + var(--mx));
          }
        }
      }
      &_4 {
        &.pratralay_active-table {
          &_1 {
            --active-table-dot-inset: var(--m) 50% 50% var(--m);
          }
          &_2 {
            --active-table-dot-inset: var(--m) 50% 50% calc(100% + var(--mx));
          }
          &_3 {
            --active-table-dot-inset: calc(100% + var(--mx)) 50% 50% var(--m);
          }
          &_4 {
            --active-table-dot-inset: calc(100% + var(--mx)) 50% 50% calc(100% + var(--mx));
          }
        }
      }
    }
  }
}
.pratralay_session-finished-btns {
  position: absolute;
  background: var(--clr-black-0-3);
  width: 3222rem;
  padding: 2rem;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
@media (max-width: 1280px) {
  .centered-stats {
    &_content {
      &_tables {
        &_2 {
          &.pratralay_active-table {
            &_1 {
              --active-table-dot-inset: var(--m) 50% 50% var(--m);
            }
            &_2 {
              --active-table-dot-inset: calc(100% + var(--mx)) 50% 50% var(--m);
            }
          }
        }
      }
    }
  }
}
</style>
